import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ChevronDown } from "lucide-react";
import { IMAGES } from "../../lib/content";

const EASE = [0.16, 1, 0.3, 1];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const textOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const scrollTo = (id) =>
    document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative h-screen w-full overflow-hidden bg-forest-deep"
    >
      <motion.div style={{ y }} className="absolute inset-0">
        <img
          src={IMAGES.heroForest}
          alt="Native forest of Aotearoa at sunrise, wrapped in soft mist"
          className="h-full w-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-forest-deep/55" />
      <div className="absolute inset-0 bg-gradient-to-t from-forest-deep via-forest-deep/20 to-forest-deep/50" />
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 65% 55% at 50% 50%, rgba(10,28,18,0.55), rgba(10,28,18,0) 75%)",
        }}
      />

      <motion.div
        style={{ opacity: textOpacity }}
        className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6"
      >
        <h1 className="sr-only">Ngā Kākano Wāhine</h1>
        <motion.img
          src="/nkw-logo-512.png"
          alt="Ngā Kākano Wāhine — Seeding Potential. Growing Independence."
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.6, ease: EASE, delay: 0.25 }}
          className="w-40 h-40 sm:w-48 sm:h-48 md:w-56 md:h-56 lg:w-64 lg:h-64 rounded-full drop-shadow-[0_10px_40px_rgba(10,28,18,0.55)]"
        />

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.3, ease: EASE, delay: 0.7 }}
          className="mt-8 max-w-2xl text-cream/85 text-base sm:text-lg md:text-xl font-light leading-relaxed"
        >
          An emerging social enterprise being developed in Ōtautahi Christchurch —
          creating pathways toward self-employment, entrepreneurship and business
          ownership for wāhine who have faced significant barriers.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.3, ease: EASE, delay: 1 }}
          className="mt-8 flex flex-col sm:flex-row items-center gap-4"
        >
          <button
            data-testid="hero-join-btn"
            onClick={() => scrollTo("#connect")}
            className="bg-cream text-forest px-9 py-4 text-sm tracking-wide hover:bg-gold transition-colors duration-300"
          >
            Register Your Interest
          </button>
          <button
            data-testid="hero-korero-btn"
            onClick={() => scrollTo("#connect")}
            className="border border-cream/50 text-cream px-9 py-4 text-sm tracking-wide hover:border-gold hover:text-gold transition-colors duration-300"
          >
            Follow Our Journey
          </button>
        </motion.div>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollTo("#why")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-cream/60"
      >
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2.4, ease: "easeInOut" }}
          className="block"
        >
          <ChevronDown strokeWidth={1.5} size={22} />
        </motion.span>
      </motion.button>
    </section>
  );
};
